import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { useSeasonStore } from '@/stores/season-store';
import { useTemplateStore } from '@/stores/template-store';
import { getStateName } from '@/utils/us-states';
import { useTaskStore } from '@/stores/task-store';
import { TemplateSelectionFlow } from '@/components/templates/TemplateSelectionFlow';
import { CountdownWidget } from '@/components/dashboard/CountdownWidget';
import { seasonStatus } from '@/utils/countdown-utils';
import { getTaskStatus, formatRelativeDueDate } from '@/utils/task-display-utils';
import { Button } from '@/components/common/Button';
import type { Task } from '@/types/task';
import type { SeasonTaskStats } from '@/services/dashboard-service';

function weaponLabel(weapon: string): string {
  const map: Record<string, string> = {
    gun: 'Gun',
    bow: 'Bow',
    muzzleloader: 'Muzzleloader',
    crossbow: 'Crossbow',
    youth: 'Youth',
    any: 'Any',
    other: 'Other',
  };
  return map[weapon] ?? weapon;
}


function TaskSummarySection({ seasonId, onGenerateTasks }: { seasonId: string; onGenerateTasks: () => void }) {
  const { tasks, loadTasks, completeTask, uncompleteTask } = useTaskStore();
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    void loadTasks(seasonId);
  }, [seasonId, loadTasks]);

  const seasonTasks = tasks.filter((t) => t.seasonId === seasonId);
  const completedCount = seasonTasks.filter((t) => t.completed).length;
  const overdueCount = seasonTasks.filter((t) => getTaskStatus(t, today) === 'overdue').length;

  // Next 3 upcoming incomplete tasks sorted by due date
  const previewTasks = [...seasonTasks]
    .filter((t) => !t.completed)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .slice(0, 3);

  async function handleToggle(task: Task) {
    if (task.completed) {
      await uncompleteTask(task.id);
    } else {
      await completeTask(task.id);
    }
  }

  if (seasonTasks.length === 0) {
    return (
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3" data-testid="task-section-empty">
        <p className="field-label">
          Checklist
        </p>
        <p className="text-body-sm text-[var(--color-text-secondary)]">
          Generate a checklist to start tracking tasks
        </p>
        <button
          onClick={onGenerateTasks}
          className="text-body-sm text-[var(--color-accent)] hover:underline text-left"
          data-testid="season-generate-tasks"
        >
          Generate tasks from a template →
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card overflow-hidden" data-testid="task-section">
      <div className="px-4 pt-4 pb-3 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between">
          <p className="field-label">
            Checklist
          </p>
          <Link
            to={`/seasons/${seasonId}/tasks`}
            className="text-body-xs text-[var(--color-accent)] hover:underline"
            data-testid="view-all-tasks-link"
          >
            View All →
          </Link>
        </div>
        <p className="text-body-xs text-[var(--color-text-muted)] mt-1" data-testid="task-summary-text">
          {seasonTasks.length} tasks · {completedCount} complete{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
        </p>
      </div>

      {/* Preview task rows */}
      {previewTasks.map((task) => {
        const isCompleted = task.completed;
        return (
          <div
            key={task.id}
            className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-border)] last:border-0"
            data-testid={`preview-task-${task.id}`}
          >
            <button
              type="button"
              onClick={() => void handleToggle(task)}
              aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
              className={`flex-shrink-0 w-[18px] h-[18px] rounded-sm flex items-center justify-center transition-colors ${
                isCompleted ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-elevated)] hover:bg-[var(--color-surface)]'
              }`}
              data-testid={`preview-checkbox-${task.id}`}
            >
              {isCompleted && (
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
                  <path d="M1 4.5L4 7.5L10 1.5" stroke="#f0ede8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-body-xs truncate ${isCompleted ? 'line-through text-[var(--color-text-muted)]' : 'text-[var(--color-text-primary)]'}`}>
                {task.title}
              </p>
            </div>
            <span className="text-body-xs text-[var(--color-text-muted)] flex-shrink-0">
              {formatRelativeDueDate(task.dueDate, today)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function SeasonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getSeason, removeSeason, loadSeasons, seasons, setPrimary } = useSeasonStore();
  const { templates, loadTemplates } = useTemplateStore();
  // Subscribe to task store so CountdownWidget task progress updates reactively (DB2-10)
  const { tasks } = useTaskStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingPrimary, setIsSettingPrimary] = useState(false);
  const [showSelectionFlow, setShowSelectionFlow] = useState(false);

  // Ensure seasons and templates are loaded if navigating directly to this URL
  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
    if (templates.length === 0) void loadTemplates();
  }, [seasons.length, loadSeasons, templates.length, loadTemplates]);

  const season = id ? getSeason(id) : undefined;

  if (!season) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Season not found.</p>
        <Link to="/seasons">
          <Button variant="ghost">Back to Seasons</Button>
        </Link>
      </div>
    );
  }

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    try {
      await removeSeason(id);
      navigate('/seasons');
    } catch {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleSetPrimary() {
    if (!id) return;
    setIsSettingPrimary(true);
    try {
      await setPrimary(id);
    } catch {
      // Error logged in store
    } finally {
      setIsSettingPrimary(false);
    }
  }

  const matchingTemplate = templates.find(
    (t) => t.weaponType === season.weaponType && t.isBuiltIn,
  );

  function handleGenerateTasks() {
    if (matchingTemplate) {
      setShowSelectionFlow(true);
    } else {
      navigate('/prep/templates');
    }
  }

  const status = seasonStatus(season);

  // Compute live task stats for the shared CountdownWidget (DB2-10)
  const today = new Date().toISOString().slice(0, 10);
  const seasonTasks = tasks.filter((t) => t.seasonId === season.id);
  const liveTaskStats: SeasonTaskStats = {
    seasonId: season.id,
    seasonName: season.name,
    total: seasonTasks.length,
    completed: seasonTasks.filter((t) => t.completed).length,
    overdue: seasonTasks.filter((t) => getTaskStatus(t, today) === 'overdue').length,
    blocked: seasonTasks.filter((t) => getTaskStatus(t, today) === 'blocked').length,
  };

  const statusPill = {
    upcoming: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30',
    active: 'bg-[var(--color-done-bg)] text-[var(--color-done)] ring-1 ring-[var(--color-done)]/30',
    ended: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]',
  }[status];

  const statusLabel = { upcoming: 'Upcoming', active: 'Active', ended: 'Ended' }[status];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/seasons"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back to seasons"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)] flex-1 truncate">
          {season.name}
        </h1>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {season.isPrimary && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30" data-testid="primary-badge">
              Primary
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium ${statusPill}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Hero: countdown widget — uses shared component for live task stats (DB2-10) */}
      <CountdownWidget
        season={season}
        taskStats={liveTaskStats}
        variant="compact"
      />

      {/* Season info card */}
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3">
        <p className="field-label">
          Season Details
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <p className="text-body-xs text-[var(--color-text-muted)] mb-0.5">State</p>
            <p className="text-body-sm text-[var(--color-text-primary)]">{getStateName(season.state)}</p>
          </div>
          <div>
            <p className="text-body-xs text-[var(--color-text-muted)] mb-0.5">Weapon Type</p>
            <p className="text-body-sm text-[var(--color-text-primary)]">{weaponLabel(season.weaponType)}</p>
          </div>
          <div>
            <p className="text-body-xs text-[var(--color-text-muted)] mb-0.5">Opener Date</p>
            <p className="text-body-sm text-[var(--color-text-primary)]">
              {format(parseISO(season.openerDate), 'MMMM d, yyyy')}
            </p>
          </div>
          {season.closerDate && (
            <div>
              <p className="text-body-xs text-[var(--color-text-muted)] mb-0.5">Closer Date</p>
              <p className="text-body-sm text-[var(--color-text-primary)]">
                {format(parseISO(season.closerDate), 'MMMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Task summary section */}
      {id && <TaskSummarySection seasonId={id} onGenerateTasks={handleGenerateTasks} />}

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        <Link to={`/seasons/${season.id}/edit`} className="flex-1">
          <Button variant="ghost" className="w-full">Edit Season</Button>
        </Link>
        {/* J-07: Only show "Set as Primary" when user has 2+ seasons.
            With one season, primary is implicit — hide the toggle. */}
        {seasons.length >= 2 && !season.isPrimary && (
          <Button
            variant="ghost"
            onClick={() => void handleSetPrimary()}
            disabled={isSettingPrimary}
            data-testid="season-set-primary"
            data-primary-toggle-available="true"
          >
            {isSettingPrimary ? 'Setting…' : 'Set as Primary'}
          </Button>
        )}
        {status === 'ended' && (
          <Link to={`/seasons/${season.id}/archive`} data-testid="view-archive-btn">
            <Button variant="ghost">View Archive</Button>
          </Link>
        )}
        <Button
          variant="danger"
          onClick={() => setShowDeleteConfirm(true)}
          data-testid="season-delete-button"
        >
          Delete
        </Button>
      </div>

      {/* Template selection flow */}
      {showSelectionFlow && matchingTemplate && (
        <TemplateSelectionFlow
          templateId={matchingTemplate.id}
          templateName={matchingTemplate.name}
          weaponType={matchingTemplate.weaponType}
          preselectedSeasonId={season.id}
          onClose={() => setShowSelectionFlow(false)}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60">
          <div className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-xl shadow-card-lg w-full max-w-sm p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
                Delete Season?
              </h2>
              <p className="text-body-sm text-[var(--color-text-secondary)]">
                This will permanently delete <strong className="text-[var(--color-text-primary)]">{season.name}</strong> and all tasks associated with it. This cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1"
                data-testid="confirm-dialog-confirm"
              >
                {isDeleting ? 'Deleting...' : 'Delete Season'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                data-testid="confirm-dialog-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
